import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  ArrowLeft,
  TrendingDown,
  Wallet,
  Calendar,
  MapPin,
  Receipt,
} from "lucide-react";
import { toast } from "sonner";

import { API } from "../utils/api";

export default function Expenses() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
  }, [user, authLoading, navigate]);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stats/expenses?year=${year}`);
      setData(res.data);
    } catch (_e) {
      toast.error("No se pudieron cargar los gastos");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    if (user) fetchExpenses();
  }, [user, fetchExpenses]);

  if (authLoading || (loading && !data)) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!user) return null;

  const a = data?.annual || { gross: 0, bookings_count: 0, avg_per_booking: 0 };

  return (
    <div className="pt-20 min-h-screen" data-testid="expenses-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
            data-testid="expenses-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Gastos</h1>
            <p className="text-gray-500 text-sm mt-1">
              Desglose de tu gasto en reservas de plazas
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setYear((y) => y - 1)}
              className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
              data-testid="expenses-prev-year"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span
              className="text-white font-bold text-lg min-w-[52px] text-center"
              data-testid="expenses-year"
            >
              {year}
            </span>
            <button
              onClick={() => setYear((y) => y + 1)}
              className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
              data-testid="expenses-next-year"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#231f30] rounded-2xl border border-rose-500/20 p-5" data-testid="card-total">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-rose-500/10 flex items-center justify-center">
                <TrendingDown className="w-4 h-4 text-rose-400" />
              </div>
              <span className="text-gray-500 text-sm">Total Gastado</span>
            </div>
            <p className="text-2xl font-bold text-rose-400">
              {a.gross.toFixed(2)} <span className="text-lg text-rose-400/60">€</span>
            </p>
          </div>
          <div className="bg-[#231f30] rounded-2xl border border-white/5 p-5" data-testid="card-bookings">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-gray-400" />
              </div>
              <span className="text-gray-500 text-sm">Reservas Pagadas</span>
            </div>
            <p className="text-2xl font-bold text-white">{a.bookings_count}</p>
          </div>
          <div className="bg-[#231f30] rounded-2xl border border-white/5 p-5" data-testid="card-avg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                <Wallet className="w-4 h-4 text-gray-400" />
              </div>
              <span className="text-gray-500 text-sm">Gasto Medio</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {a.avg_per_booking.toFixed(2)} <span className="text-lg text-gray-500">€</span>
            </p>
          </div>
        </div>

        {/* Quarterly table */}
        <div
          className="bg-[#231f30] rounded-2xl border border-white/5 overflow-hidden mb-6"
          data-testid="expenses-quarterly-section"
        >
          <div className="flex items-center gap-3 p-5 border-b border-white/5">
            <Receipt className="w-5 h-5 text-rose-400" />
            <h2 className="text-lg font-semibold text-white">Desglose Trimestral</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-gray-500 font-normal py-3 px-5">Trimestre</th>
                  <th className="text-right text-gray-500 font-normal py-3 px-3">Reservas</th>
                  <th className="text-right text-gray-500 font-normal py-3 px-3">Gasto Medio</th>
                  <th className="text-right text-gray-500 font-normal py-3 px-5">Total</th>
                </tr>
              </thead>
              <tbody>
                {data?.quarters?.map((q) => (
                  <tr
                    key={q.quarter}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="py-4 px-5">
                      <span className="text-white font-medium">{q.label}</span>
                      <span className="text-gray-600 text-xs ml-2">{q.months}</span>
                    </td>
                    <td className="text-right text-gray-400 py-4 px-3">{q.bookings_count}</td>
                    <td className="text-right text-gray-400 py-4 px-3">
                      {q.bookings_count > 0 ? `${q.avg_per_booking.toFixed(2)} €` : "—"}
                    </td>
                    <td className="text-right text-rose-400 font-medium py-4 px-5">
                      {q.gross.toFixed(2)} €
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-rose-500/5">
                  <td className="py-4 px-5">
                    <span className="text-white font-bold">Total {year}</span>
                  </td>
                  <td className="text-right text-white font-medium py-4 px-3">{a.bookings_count}</td>
                  <td className="text-right text-gray-400 py-4 px-3">
                    {a.bookings_count > 0 ? `${a.avg_per_booking.toFixed(2)} €` : "—"}
                  </td>
                  <td className="text-right text-rose-400 font-bold py-4 px-5">
                    {a.gross.toFixed(2)} €
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Top spots where you spent the most */}
        {data?.top_spots?.length > 0 && (
          <div
            className="bg-[#231f30] rounded-2xl border border-white/5 overflow-hidden"
            data-testid="expenses-top-spots"
          >
            <div className="flex items-center gap-3 p-5 border-b border-white/5">
              <MapPin className="w-5 h-5 text-violet-400" />
              <h2 className="text-lg font-semibold text-white">
                Plazas en las que más has gastado
              </h2>
            </div>
            <div className="divide-y divide-white/5">
              {data.top_spots.map((s, idx) => (
                <Link
                  key={s.spot_id}
                  to={`/spot/${s.spot_id}`}
                  className="flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors"
                >
                  <span className="w-7 h-7 rounded-full bg-violet-500/10 text-violet-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{s.title}</p>
                    {s.city && (
                      <p className="text-gray-500 text-xs mt-0.5 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {s.city}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-rose-400 font-semibold">{s.total.toFixed(2)} €</p>
                    <p className="text-gray-500 text-xs">{s.count} reserva(s)</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {data?.top_spots?.length === 0 && a.bookings_count === 0 && (
          <div className="text-center py-12 text-gray-500" data-testid="expenses-empty">
            <Wallet className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Aún no has hecho ninguna reserva pagada en {year}.</p>
            <Link
              to="/explore"
              className="inline-block mt-4 text-violet-400 hover:text-violet-300 text-sm underline"
            >
              Explorar plazas disponibles
            </Link>
          </div>
        )}

        <p className="text-gray-600 text-xs mt-6 leading-relaxed px-1">
          * Solo se cuentan reservas con pago confirmado. Las cancelaciones quedan excluidas.
        </p>
      </div>
    </div>
  );
}
